import React from 'react';
import { Form, Input, Modal, Select, message } from 'antd';
import { ITeamMember } from 'types/operation';
import { ERoleCode } from 'types/enumItems';
import s from 'components/AddAndEditStaff/s.module.less';
import commonS from 'styles/common.module.less';
import { REQUIRED_FIELD } from 'constants/common';
import UsPhoneInput from 'components/form/USPhoneInput';
import FormValidationHelper from 'utils/validation';
import {
    addOpsStaff,
    checkOpsStaffEmail,
    updateStaffMembership,
} from 'api/operation/group';

type Props = {
    show: boolean;
    organizationId: number;
    item?: ITeamMember;
    providerMembers?: ITeamMember[];
    onCancel?: () => void;
    onSuccess?: () => void;
};

type PermissionCategory = {
    title: string;
    items: string[];
};

const ROLE_PERMISSIONS: Partial<Record<ERoleCode, PermissionCategory[]>> = {
    [ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT]: [
        {
            title: 'Patient care',
            items: ['Patient information', 'Patient communications', 'Patient reviews', 'Schedule & edit appointments'],
        },
        {
            title: 'Billing & payments',
            items: ['Invoices', 'Financials (Payouts & marketing investment)'],
        },
    ],
    [ERoleCode.GROUP_CLINICAL_ASSISTANT]: [
        {
            title: 'Patient care',
            items: ['Patient information', 'Patient communications', 'Patient reviews', 'Schedule & edit appointments'],
        },
        {
            title: 'Clinical care',
            items: ['Treatment plans', 'Prescriptions', 'Text visits'],
        },
    ],
    [ERoleCode.GROUP_PRACTICE_MANAGER]: [
        {
            title: 'Patient care',
            items: ['Patient information', 'Patient communications', 'Patient reviews', 'Schedule & edit appointments'],
        },
        {
            title: 'Billing & payments',
            items: ['Invoices', 'Financials (Payouts & marketing investment)'],
        },
        {
            title: 'Practice management',
            items: [
                'Homepage',
                'Practice profile',
                'Service templates',
                'Medications settings',
                'Marketing (Pro-listings)',
                'Appointment policies (No-shows, late cancellations and rescheduling)',
            ],
        },
        {
            title: 'Team & access',
            items: ['Add providers', 'Settings'],
        },
    ],
};

const ROLE_ID_MAP: Record<string, number> = {
    [ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT]: 17,
    [ERoleCode.GROUP_CLINICAL_ASSISTANT]: 18,
    [ERoleCode.GROUP_PRACTICE_MANAGER]: 19,
};

type RoleConfig = {
    key: ERoleCode;
    name: string;
    description: string;
};

const ROLE_CONFIGS: RoleConfig[] = [
    {
        key: ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT,
        name: 'Administrative assistant',
        description: 'Supports front-desk operations including scheduling, patient communication, and billing.',
    },
    {
        key: ERoleCode.GROUP_CLINICAL_ASSISTANT,
        name: 'Clinical assistant (MA)',
        description: 'Supports providers with clinical documentation, labs, and prescriptions.',
    },
    {
        key: ERoleCode.GROUP_PRACTICE_MANAGER,
        name: 'Practice manager',
        description:
            'Full access to practice operations, billing, and settings. Manages team workflows and administrative tools.',
    },
];

const ALL_PROVIDERS_OPTION = { label: 'All providers', value: 'all' };

const ChevronDownIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 9L12 15L18 9" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const PermissionCategoryPanel: React.FC<{ category: PermissionCategory }> = ({ category }) => {
    const [open, setOpen] = React.useState(true);

    return (
        <div className={s.permissionCategory}>
            <div className={s.categoryHeader} onClick={() => setOpen(!open)}>
                <span className={s.categoryTitle}>{category.title}</span>
                <span className={`${s.chevron} ${open ? s.chevronOpen : ''}`}>
                    <ChevronDownIcon />
                </span>
            </div>
            {open && (
                <div className={s.categoryItems}>
                    {category.items.map((item) => (
                        <div key={item} className={s.categoryItem}>
                            {item}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const OpsAddEditStaff = (props: Props) => {
    const { show, organizationId, onCancel, item, providerMembers = [] } = props;
    const [submitting, setSubmitting] = React.useState(false);
    const [form] = Form.useForm();
    const isEdit = !!item?.id;
    const [selectedRole, setSelectedRole] = React.useState<ERoleCode>(ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT);
    const [isAllProviders, setIsAllProviders] = React.useState(true);
    const [assignedProviderIds, setAssignedProviderIds] = React.useState<number[]>([]);

    React.useEffect(() => {
        if (show && item) {
            form.setFieldsValue({
                firstName: item.firstName,
                lastName: item.lastName,
                email: item.email,
                phoneNumber: item.phoneNumber,
            });
            if (item.roleCode) setSelectedRole(item.roleCode as ERoleCode);
            setIsAllProviders(item.isAllProviders);
            if (!item.isAllProviders && item.assignedProviders?.length) {
                setAssignedProviderIds(item.assignedProviders.map((p) => p.providerId));
            } else {
                setAssignedProviderIds([]);
            }
        }
        if (!show) {
            form.resetFields();
            setSelectedRole(ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT);
            setIsAllProviders(true);
            setAssignedProviderIds([]);
        }
    }, [show, item, form]);

    const providerOptions = React.useMemo(() => {
        const providers = providerMembers.map((m) => ({
            label: `${m.firstName} ${m.lastName}`,
            value: String(m.providerId),
        }));
        return [ALL_PROVIDERS_OPTION, ...providers];
    }, [providerMembers]);

    const permissions = selectedRole ? ROLE_PERMISSIONS[selectedRole] || [] : [];

    const onSubmit = async () => {
        try {
            const formValues = await form.validateFields();
            const roleId = ROLE_ID_MAP[selectedRole];
            if (!roleId) {
                message.error('Invalid role selected');
                return;
            }
            const isAdmin = selectedRole === ERoleCode.GROUP_PRACTICE_MANAGER;
            const providerIds = isAllProviders ? [] : assignedProviderIds;

            setSubmitting(true);

            if (isEdit && item) {
                const res = await updateStaffMembership(item.userId, item.id, {
                    phoneNumber: formValues.phoneNumber || null,
                    roleId: item.roleId !== roleId ? roleId : null,
                    isAdmin: isAdmin,
                    providerIds: isAllProviders ? [] : providerIds,
                });
                if (res.error) return;

                message.success('Team member updated');
                props.onSuccess?.();
                return;
            }

            const res = await addOpsStaff(organizationId, {
                firstName: formValues.firstName,
                lastName: formValues.lastName,
                email: formValues.email,
                phoneNumber: formValues.phoneNumber,
                roleId,
                isAdmin,
                providerIds,
            });

            if (res.error) return;
            message.success('Invitation sent');
            props.onSuccess?.();
        } catch (e) {
            if (e && typeof e === 'object' && 'errorFields' in e) {
                return;
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            title={isEdit ? 'Edit staff' : 'Add staff'}
            closable
            className={commonS.modalFixHeightWrap}
            width="810px"
            onCancel={() => onCancel?.()}
            okText={isEdit ? 'Save' : 'Send invite'}
            cancelText="Cancel"
            open={show}
            onOk={onSubmit}
            destroyOnClose
            okButtonProps={{ loading: submitting }}
            zIndex={99999}
        >
            <Form
                form={form}
                name="opsTeamMember"
                className={commonS.formStyle1}
                autoComplete="off"
                layout="vertical"
                scrollToFirstError
            >
                <div className={s.modalWrap}>
                    <h3 className={s.sectionTitle}>Basic information</h3>
                    <div className={s.row}>
                        <div className={s.rowItem}>
                            <Form.Item
                                style={{ width: '368px' }}
                                label="First name"
                                name="firstName"
                                rules={[{ required: true, message: REQUIRED_FIELD }]}
                            >
                                <Input maxLength={25} />
                            </Form.Item>
                        </div>
                        <div className={s.rowItem}>
                            <Form.Item
                                style={{ width: '368px' }}
                                label="Last name"
                                name="lastName"
                                rules={[{ required: true, message: REQUIRED_FIELD }]}
                            >
                                <Input maxLength={25} />
                            </Form.Item>
                        </div>
                    </div>
                    <div className={s.row}>
                        <div className={s.rowItem}>
                            <Form.Item
                                style={{ width: '368px' }}
                                label="Email"
                                name="email"
                                validateFirst
                                validateTrigger={['onChange', 'onBlur']}
                                rules={[
                                    { required: !isEdit, message: REQUIRED_FIELD },
                                    { type: 'email', message: 'Please enter a valid email' },
                                    {
                                        validateTrigger: 'onBlur',
                                        validator: async (_, value) => {
                                            if (isEdit || !value?.trim()) return;
                                            try {
                                                const { data } = await checkOpsStaffEmail(organizationId, value.trim());
                                                if (data?.code !== 200 || !data?.data) return;
                                                const result = data.data;
                                                if (!result.addable) {
                                                    throw new Error(result.message || 'This email cannot be added');
                                                }
                                            } catch (err: any) {
                                                if (err?.message) throw err;
                                            }
                                        },
                                    },
                                ]}
                            >
                                <Input disabled={isEdit} />
                            </Form.Item>
                        </div>
                        <div className={s.rowItem}>
                            <Form.Item
                                style={{ width: '368px' }}
                                label="Phone number"
                                name="phoneNumber"
                                validateFirst
                                rules={[
                                    FormValidationHelper.validateUSPhoneNumber('Please enter a 10-digit phone number'),
                                ]}
                            >
                                <UsPhoneInput />
                            </Form.Item>
                        </div>
                    </div>

                    <div className={s.divider} />

                    <h3 className={s.sectionTitle}>Select role</h3>
                    <div className={s.roleGrid}>
                        {ROLE_CONFIGS.map((role) => {
                            const isSelected = selectedRole === role.key;
                            const cardClass = [s.roleCard, isSelected ? s.roleCardSelected : '']
                                .filter(Boolean)
                                .join(' ');

                            return (
                                <div
                                    key={role.key}
                                    className={cardClass}
                                    onClick={() => setSelectedRole(role.key)}
                                >
                                    <div className={s.roleCardHeader}>
                                        <span className={s.roleName}>{role.name}</span>
                                    </div>
                                    <div className={s.roleDescription}>{role.description}</div>
                                </div>
                            );
                        })}
                    </div>

                    <div className={s.divider} />

                    <h3 className={s.sectionTitle}>Permissions by category</h3>
                    {permissions.length > 0 ? (
                        <div className={s.permissionsSection}>
                            {permissions.map((category) => (
                                <PermissionCategoryPanel key={category.title} category={category} />
                            ))}
                        </div>
                    ) : (
                        <div className={s.noPermissions}>Select a role to view permissions</div>
                    )}

                    <div className={s.divider} />

                    <h3 className={s.sectionTitle}>Provider assignment</h3>
                    <p className={s.providerAssignDesc}>Select which providers this team member can access.</p>
                    <div className={s.providerAssignField}>
                        <div className={s.providerAssignLabel}>Assign to provider</div>
                        <Select
                            style={{ width: '368px' }}
                            mode="multiple"
                            value={isAllProviders ? ['all'] : assignedProviderIds.map(String)}
                            onChange={(values: string[]) => {
                                const wasAll = isAllProviders;
                                const nowHasAll = values.includes('all');
                                if (nowHasAll && !wasAll) {
                                    setIsAllProviders(true);
                                    setAssignedProviderIds([]);
                                } else if (wasAll && values.length > 1) {
                                    const filtered = values.filter((v) => v !== 'all');
                                    setIsAllProviders(false);
                                    setAssignedProviderIds(filtered.map(Number));
                                } else if (!nowHasAll) {
                                    setIsAllProviders(false);
                                    setAssignedProviderIds(values.map(Number));
                                }
                            }}
                            options={providerOptions}
                            placeholder="Select providers"
                        />
                    </div>
                </div>
            </Form>
        </Modal>
    );
};

export default OpsAddEditStaff;
